/* @flow */

import React from 'react';
import { inject, observer } from 'mobx-react';
import { observable, computed, reaction } from 'mobx';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import Loading from 'components/Loading/Loading';
import NotificationStore, { Subscription } from 'stores/NotificationStore';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { filterNotificationsBySearch } from 'utils/NotificationUtils';
import InfiniteLoader from 'components/InfiniteLoader';

import NotificationPanel from './NotificationPanel/NotificationPanel';
import styles from './Notifications.module.scss';

export type NotificationsProps = {
  searchTerm: ?string,
};

type InjectedProps = {
  notificationStore: NotificationStore,
} & TranslatorProps;

@translate()
@inject('notificationStore')
@observer
export default class Notifications extends ComponentWithInjectedProps<
  NotificationsProps,
  {},
  InjectedProps
> {
  _reactionDisposers: Function[] = [];

  @observable notificationsSubscription: ?Subscription;

  @computed get data() {
    const { searchTerm, t } = this.props;

    if (this.notificationsSubscription) {
      if (searchTerm) {
        return filterNotificationsBySearch(this.notificationsSubscription.data, searchTerm, t);
      }

      return this.notificationsSubscription.data;
    }

    return [];
  }

  componentDidMount() {
    const { notificationStore } = this.props;

    this.notificationsSubscription = notificationStore.subscribeToNotifications(
      { type: 'notifications', urlParams: [], searchParams: [] },
      { background: false },
      true
    );

    this._reactionDisposers.push(
      reaction(() => this.data.length, () => setImmediate(this.handleScroll))
    );
  }

  componentWillUnmount() {
    this.clearCurrentSubscription();
  }

  clearCurrentSubscription = () => {
    if (this.notificationsSubscription) {
      this.props.notificationStore.unsubscribeFromNotifications(this.notificationsSubscription.id);
      this.notificationsSubscription = undefined;
    }
  };

  handleRequestMoreItems = async () => {
    if (
      this.notificationsSubscription &&
      this.notificationsSubscription.pagesInView !== this.notificationsSubscription.nextPagesInView
    ) {
      await this.notificationsSubscription.setPagesInView(
        this.notificationsSubscription.nextPagesInView,
        true
      );
    }
  };

  renderNotification = (n: Object) => {
    const { notificationStore } = this.props;

    return (
      <NotificationPanel
        key={n.event_id}
        notification={n}
        handleAction={notificationStore.handleAction}
      />
    );
  };

  render() {
    const { t } = this.props;

    return (
      <InfiniteLoader
        loading={!this.notificationsSubscription || this.notificationsSubscription.loading}
        onRequestMoreItems={this.handleRequestMoreItems}
      >
        {this.data.map(notification => this.renderNotification(notification))}

        {this.notificationsSubscription &&
          !this.notificationsSubscription.loading &&
          !this.data.length && (
            <p className={styles.noResults}>
              {t('general:notifications.no_results.notifications')}
            </p>
          )}
      </InfiniteLoader>
    );
  }
}
